import { publicApi, useApi } from "../api";

// Objeto estático para métodos públicos que NO requieren autenticación
export const authService = {
  login: async (email, password, captchaToken) => {
    try {
      const response = await publicApi.post('/login', {
        email,
        password,
        captchaToken,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  validateToken: async (token) => {
    try {
      const response = await publicApi.post('/validate-token', { token });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  refreshToken: async (refreshToken) => {
    try {
      const response = await publicApi.post('/refresh-token', { refreshToken });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
};

// Hook para métodos que SÍ requieren autenticación
export const useAuthService = () => {
  const api = useApi();

  return {
    register: async (userData) => {
      try {
        // Función para procesar el archivo y convertirlo a Base64
        const processFile = async (file) => {
          if (!file) return null;
          if (typeof file === "string" && file.startsWith("data:")) {
            return file.split(",")[1];
          }
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result.split(",")[1]);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        };

        // Procesar documentos
        const processDocuments = async (documents) => {
          if (!documents || !Array.isArray(documents)) {
            return [];
          }

          try {
            const processedDocs = await Promise.all(
              documents.map(async (doc) => {
                if (!doc || !doc.file) return null;

                try {
                  const base64Content = await processFile(doc.file);
                  return {
                    documentTypeId: doc.docType,
                    fileName: doc.fileName,
                    originalName: doc.file.name,
                    fileType: doc.file.type,
                    fileSize: doc.file.size,
                    content: base64Content,
                    expeditionDate: doc.expeditionDate || null,
                    expirationDate: doc.expirationDate || null,
                    originalNameDescription: doc.description || "",
                    required: Boolean(doc.required),
                  };
                } catch (docError) {
                  return null;
                }
              })
            );

            const validProcessedDocs = processedDocs.filter((doc) => doc !== null);

            // Procesar la foto de perfil si existe
            if (userData?.personalInfo?.profilePhoto) {
              let profilePhotoContent;

              if (userData.personalInfo.profilePhoto.content) {
                profilePhotoContent = userData.personalInfo.profilePhoto.content.split(",")[1];
              } else {
                profilePhotoContent = await processFile(userData.personalInfo.profilePhoto.file);
              }

              validProcessedDocs.push({
                documentTypeId: userData.personalInfo.profilePhoto.documentTypeId,
                fileName: userData.personalInfo.profilePhoto.name,
                originalName: userData.personalInfo.profilePhoto.name,
                fileType: userData.personalInfo.profilePhoto.type,
                fileSize: userData.personalInfo.profilePhoto.size,
                content: profilePhotoContent,
                originalNameDescription: "Foto de perfil",
                required: true,
              });
            }

            return validProcessedDocs;
          } catch (error) {
            return [];
          }
        };

        // Procesar datos
        const processedPersonalInfo = {
          firstName: userData.personalInfo.firstName,
          firstLastName: userData.personalInfo.firstLastName,
          secondLastName: userData.personalInfo.secondLastName,
          birthDate: userData.personalInfo.birthDate,
          documentNumber: userData.personalInfo.documentNumber,
          documentExpDate: userData.personalInfo.documentExpDate,
          documentTypeId: userData.personalInfo.documentTypeId,
          documentExpeditionPlace: userData.personalInfo.documentExpeditionPlace,
          nationality: userData.personalInfo.nationality,
          limitedNotifications: userData.personalInfo.limitedNotifications,
          ownFleet: userData.personalInfo.ownFleet,
          department: userData.personalInfo.department,
          bloodType: userData.personalInfo.bloodType,
          jobPosition: userData.personalInfo.jobPosition === 'custom' 
            ? userData.personalInfo.customJobPosition 
            : userData.personalInfo.jobPosition,
          isCompany: userData.personalInfo.isCompany || false,
          nitExtension: userData.personalInfo.nitExtension || null,
        };

        const processedContactInfo = {
          primaryPhoneNumber: userData.contactInfo.primaryPhoneNumber,
          primaryPhoneCountryId: userData.contactInfo.primaryPhoneCountryId,
          secondaryPhoneNumber: userData.contactInfo.secondaryPhoneNumber,
          secondaryPhoneCountryId: userData.contactInfo.secondaryPhoneCountryId,
          email: userData.contactInfo.email,
          contactName: userData.contactInfo.contactName,
          emergencyContactName: userData.contactInfo.emergencyContactName,
          emergencyContactPhone: userData.contactInfo.emergencyContactPhone,
          eps: userData.contactInfo.eps,
          arl: userData.contactInfo.arl,
        };

        const processedDocuments = await processDocuments(userData.documents);

        const processedUserData = {
          personalInfo: processedPersonalInfo,
          contactInfo: processedContactInfo,
          roleId: userData.roleId,
          location: userData.contactInfo.location,
          documents: processedDocuments,
        };

        // Usar instancia autenticada para registro
        const response = await api.post('/register', processedUserData);
        return response.data;
      } catch (error) {
        throw error.response?.data || error.message;
      }
    },
  };
};